import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Calendar } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ScoreBar } from '../components/ScoreBar'

// Mirrors the backend Pydantic shape in
// src/killacoach/api/routes/appointments.py (AppointmentRow).
interface AppointmentRow {
  call_id: number
  vici_user: string
  agent_name: string | null
  campaign_id: string | null
  lead_id: number | null
  phone_number: string | null
  call_started_at: string
  length_sec: number
  overall: number | null
}

interface AppointmentsResponse {
  total: number
  items: AppointmentRow[]
}

type RangePreset = 'today' | 'yesterday' | '7d' | '30d'

const PRESETS: ReadonlyArray<{ id: RangePreset; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7d' },
  { id: '30d', label: 'Last 30d' },
]

function startOfUtcDay(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0))
}

function rangeFor(preset: RangePreset): { since: string; until: string; label: string } {
  const now = new Date()
  const todayStart = startOfUtcDay(now)
  switch (preset) {
    case 'today': {
      return {
        since: todayStart.toISOString(),
        until: now.toISOString(),
        label: 'today',
      }
    }
    case 'yesterday': {
      const yStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000)
      const yEnd = new Date(todayStart.getTime() - 1)
      return { since: yStart.toISOString(), until: yEnd.toISOString(), label: 'yesterday' }
    }
    case '7d': {
      const since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      return { since: since.toISOString(), until: now.toISOString(), label: 'the last 7 days' }
    }
    case '30d': {
      const since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      return { since: since.toISOString(), until: now.toISOString(), label: 'the last 30 days' }
    }
  }
}

// Tiny inline fetcher — request() in api.ts is private to that module and
// the appointments endpoint is owned by sibling G5; staying local keeps the
// surface area minimal and avoids editing api.ts.
async function fetchAppointments(
  since: string,
  until: string,
  agent: string
): Promise<AppointmentsResponse> {
  const sp = new URLSearchParams({ since, until })
  if (agent) sp.set('agent', agent)
  const headers: Record<string, string> = { Accept: 'application/json' }
  let key: string | null = null
  try {
    key = localStorage.getItem('killacoach_api_key')
  } catch {
    key = null
  }
  if (key) headers.Authorization = `Bearer ${key}`
  const res = await fetch(`/api/appointments?${sp.toString()}`, { headers })
  if (!res.ok) {
    let msg = `${res.status}`
    try {
      const j = await res.json()
      if (j && typeof j === 'object' && 'error' in j) msg = String(j.error)
    } catch {
      try {
        msg = (await res.text()) || msg
      } catch {
        // keep status code
      }
    }
    throw new Error(msg)
  }
  return (await res.json()) as AppointmentsResponse
}

function fmtLen(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function scoreClass(overall: number | null): string {
  if (overall === null || overall === undefined) return 'text-slate-500'
  if (overall >= 8) return 'text-emerald-400'
  if (overall >= 6) return 'text-amber-400'
  return 'text-red-400'
}

export function AppointmentsPage() {
  const [preset, setPreset] = useState<RangePreset>('7d')
  const [agent, setAgent] = useState<string>('')

  const range = useMemo(() => rangeFor(preset), [preset])

  const { data, isLoading, error } = useQuery({
    queryKey: ['appointments', range.since, range.until, agent],
    queryFn: () => fetchAppointments(range.since, range.until, agent),
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0

  // Auto-populate agent dropdown from the unfiltered fetch — when an agent is
  // selected we'd lose the option list, so derive options from a separate
  // query that ignores the `agent` filter.
  const { data: allData } = useQuery({
    queryKey: ['appointments', 'agent-options', range.since, range.until],
    queryFn: () => fetchAppointments(range.since, range.until, ''),
  })

  const agentOptions = useMemo(() => {
    const map = new Map<string, string | null>()
    for (const r of allData?.items ?? []) {
      if (!map.has(r.vici_user)) map.set(r.vici_user, r.agent_name)
    }
    return Array.from(map.entries())
      .map(([vici_user, name]) => ({ vici_user, name }))
      .sort((a, b) => a.vici_user.localeCompare(b.vici_user))
  }, [allData])

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-2">
        <Calendar className="h-5 w-5 text-indigo-400" />
        <h1 className="text-xl font-semibold">Appointments</h1>
        <span className="ml-2 text-xs text-slate-500">
          {isLoading ? '...' : `${total} booked`}
        </span>
      </header>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm">
        <div className="flex items-center gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPreset(p.id)}
              className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                preset === p.id
                  ? 'border-indigo-600 bg-indigo-900/40 text-indigo-200'
                  : 'border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {agentOptions.length > 0 && (
          // biome-ignore lint/a11y/noLabelWithoutControl: implicit-label, child select
          <label className="ml-2 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">Agent</span>
            <select
              value={agent}
              onChange={(e) => setAgent(e.target.value)}
              className="w-56 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-200"
            >
              <option value="">All agents</option>
              {agentOptions.map((a) => (
                <option key={a.vici_user} value={a.vici_user}>
                  {a.name ? `${a.name} (${a.vici_user})` : a.vici_user}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="rounded-lg border border-slate-800 bg-gradient-to-br from-indigo-950/40 to-slate-900/40 p-5">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          Appointments booked
        </div>
        <div className="mt-1 font-mono text-3xl font-semibold text-indigo-200">{total}</div>
        <div className="mt-1 text-xs text-slate-500">in {range.label}</div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-900 bg-red-950/40 p-3 text-sm text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{(error as Error).message}</span>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/40">
        {isLoading ? (
          <div className="space-y-1 p-3">
            {Array.from({ length: 6 }, (_, i) => `skeleton-${i}`).map((key) => (
              <div key={key} className="h-10 animate-pulse rounded bg-slate-800/60" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm text-slate-400">
              No appointments booked in this range.
            </p>
            <p className="mt-1 text-xs text-slate-500">Currently showing {range.label}.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/80 text-xs text-slate-400">
                <tr>
                  <th className="px-3 py-2 text-left">Agent</th>
                  <th className="px-3 py-2 text-left">Campaign</th>
                  <th className="px-3 py-2 text-left">Phone</th>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-right">Length</th>
                  <th className="px-3 py-2 text-left">Overall</th>
                  <th className="px-3 py-2 text-right">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.call_id}
                    className="border-t border-slate-800 transition-colors hover:bg-slate-800/40"
                  >
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-100">
                        {row.agent_name ?? row.vici_user}
                      </div>
                      {row.agent_name && (
                        <div className="text-xs text-slate-500">{row.vici_user}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-300">
                      {row.campaign_id ?? <span className="text-slate-600">--</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-300">
                      {row.phone_number ?? <span className="text-slate-600">--</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-400">
                      {new Date(row.call_started_at).toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-xs text-slate-400">
                      {fmtLen(row.length_sec)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <ScoreBar value={row.overall} showLabel={false} />
                        <span className={`font-mono text-xs ${scoreClass(row.overall)}`}>
                          {row.overall === null || row.overall === undefined
                            ? '--'
                            : row.overall.toFixed(1)}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <Link
                        to={`/calls/${row.call_id}`}
                        className="text-xs text-indigo-400 hover:text-indigo-300"
                      >
                        detail →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
